import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";

import {
    filterLinkedProducts,
    isProductUnlinked,
} from "~/admin/setup/customer/agvance-linked-products";
import { useLinkedProductRestriction } from "~/admin/setup/customer/use-linked-product-restriction";
import { Loader, LoaderTypes } from "~/core";

import ProductList from "./product-list";

interface IProductOption {
    guid: string;
}

interface IProductRecordRow {
    productGuid: string;
}

const getOptionGuid = (option: IProductOption): string => option.guid;

interface IRestrictedProductListProps {
    /**
     * False leaves the list unrestricted and skips the lookup entirely. Panels whose scope is not
     * known yet must use it -- an unset orgLevelGuid falls back to the logged-in user's own org level
     */
    isEnabled?: boolean;
    /** Fired whenever the unlinked state changes. Panels use it to block Save (§5.3). */
    onUnlinkedProductsChange?: (hasUnlinkedProduct: boolean) => void;
    options: IProductOption[];
    /** Defaults to the logged-in user's company. Org Level Products supplies the selected one. */
    orgLevelGuid?: string | null;
    record: IProductRecordRow[];
    scopeCompany?: { linkedProductOnly: boolean; personalityId: number } | null;
    /** Covers the list while the restriction lookup runs, so a company change shows progress. */
    showRestrictionLoader?: boolean;
    [passThrough: string]: unknown;
}

/**
 * Wraps the shared setup ProductList with the Agvance linked-product restriction: filters what can be
 * added, retains what is on the record, marks unlinked rows, and reports upward so the host can block Save.
 */
const RestrictedProductList = ({
    isEnabled = true,
    onUnlinkedProductsChange,
    options,
    orgLevelGuid = null,
    record,
    scopeCompany = null,
    showRestrictionLoader = false,
    ...passThroughProps
}: IRestrictedProductListProps): ReactElement => {
    const { isRestrictionSettled, linkedProductGuids } = useLinkedProductRestriction({
        isEnabled,
        orgLevelGuid,
        scopeCompany,
    });

    const [currentRows, setCurrentRows] = useState<IProductRecordRow[]>(record ?? []);

    useEffect(() => {
        setCurrentRows(record ?? []);
    }, [record]);

    const recordGuids = useMemo(
        () => (currentRows ?? []).map((row) => row.productGuid).filter(Boolean),
        [currentRows]
    );

    const unlinkedProductGuids =
        linkedProductGuids == null
            ? null
            : new Set(recordGuids.filter((guid) => isProductUnlinked(guid, linkedProductGuids)));

    const hasUnlinkedProduct = Boolean(unlinkedProductGuids && unlinkedProductGuids.size > 0);

    const onUnlinkedProductsChangeRef = useRef(onUnlinkedProductsChange);
    useEffect(() => {
        onUnlinkedProductsChangeRef.current = onUnlinkedProductsChange;
    });
    useEffect(() => {
        onUnlinkedProductsChangeRef.current?.(hasUnlinkedProduct);
    }, [hasUnlinkedProduct]);

    const markedUnlinkedProductGuids = unlinkedProductGuids ?? undefined;

    const restrictedOptions = useMemo(
        () => filterLinkedProducts(options ?? [], linkedProductGuids, getOptionGuid, recordGuids),
        [options, linkedProductGuids, recordGuids]
    );

    const onTextChange = (event: unknown, nextRecord: IProductRecordRow[]): void => {
        setCurrentRows(nextRecord ?? []);
        const forward = passThroughProps.onTextChange as
            | ((event: unknown, nextRecord: IProductRecordRow[]) => void)
            | undefined;
        if (forward) {
            forward(event, nextRecord);
        }
    };

    // The loader is rendered here rather than reported up to the host on purpose. Sliding-panel
    // bodies are arrow functions rebuilt every render
    return (
        <>
            {!showRestrictionLoader || isRestrictionSettled ? null : (
                <Loader type={LoaderTypes.LINE_SCALE} />
            )}
            <ProductList
                {...passThroughProps}
                options={restrictedOptions}
                record={record}
                onTextChange={onTextChange}
                unlinkedProductGuids={markedUnlinkedProductGuids}
            />
        </>
    );
};

export default RestrictedProductList;
